import { ComponentType } from '@angular/cdk/overlay';
import { MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Quaternion, Vector3, AbstractMesh } from '@babylonjs/core';
import { FormControl } from '@angular/forms';

/** To extend window you must extend the global Window interface */
declare global {
	interface Window {
		URL: typeof URL;
		WritableStream: typeof WritableStream;
	}
}

export interface Breakpoint {
	min: string;
	max: string;
}

export interface Breakpoints {
	[propName: string]: Breakpoint;
}

export interface NetworkStatus {
	event: Event;
	status: boolean;
}

export interface GoogleMapsInfoWindow extends google.maps.InfoWindow {
	getMap?: () => google.maps.Map | null;
}

export interface DialogOptions<D> extends MatDialogConfig {
	component: ComponentType<any>;
	data?: D;
}

export interface BlindSetup {
	frames?: Frames;
	material?: Material;
	mounting?: Mounting;
	operation?: Operation;
	size?: Size;
	fixtures_color?: FixturesColor;
}

export interface Frames {
	bottom_bar: BottomBar;
	frame_color: FrameColor;
	top_style: TopStyle;
	optionals?: BottomChannel[];
}

export interface Color extends DefaultProperties {
	color: string;
	optionals?: BottomChannel[];
}

export interface FrameColor extends Color {
	color_name?: string;
	is_custom?: boolean;
	last_saved_color?: string;
}

export interface Material extends Color {
	color_name: string;
	opacity: number;
	type: string;
}

export interface FixturesColor extends Color {
	checked: boolean;
	is_default: boolean;
}

export interface Size {
	width: number;
	height: number;
}

export interface TopStyle extends DefaultProperties {
	description?: string;
	image?: string | null;
	is_default?: boolean;
	status?: string | null;
	type?: string;
}

export interface BottomBar extends TopStyle {}

export interface BottomChannel extends DefaultProperties {
	is_checked: boolean;
}

export interface Mounting extends DefaultProperties {
	checked: boolean;
	description: string;
	is_default: boolean;
}

export interface Operation extends DefaultProperties {
	default_option: string;
	is_default: boolean;
	long_name: string;
	optional: OperationOption[];
}

export interface OperationOption extends DefaultProperties {
	description: string;
	operation_id: string;
	product_type: string;
}

export interface BlindData {
	blind_id: number;
	default_name: string;
	type: string;
	name?: string;
	has_name?: boolean;
	setup?: BlindSetup;
	blind_img?: string;
	iv_img?: string;
}

export interface BlindProperties {
	id: number;
	type: string;
}

export interface DefaultProperties {
	id: number;
	name: string;
}

export interface ImageData {
	country: string;
	image: string;
	name: string;
}

export interface Retailer {
	address: string;
	car_button: string;
	country: string;
	date: string;
	display_coeff: string;
	display_count: string;
	icon: string;
	id: number;
	latitude: string;
	logo: string;
	longitude: string;
	name: string;
	page_id: string;
	phone: string;
	premium: string;
	priority: string;
	product: string;
	retailers_page: string;
	service_and_repairs: string;
	show_address: boolean;
	status: string;
	store_type: string;
	swiftee: string;
	website: string;
	zip_code: string;
	suburb: string;
	state: string;
	marker: any;
	full_address: string;
	boost?: number;
	open?: boolean;
}

export interface AccordionSelector {
	title: string;
	content: string;
	item: string;
}

export interface BoundingBoxSettings {
	scaling: Vector3;
	position: Vector3;
	rotationQuaternion: Quaternion;
	cameraRadius: number;
	isEnabled: boolean;
}

export interface BoundingBoxesSettings {
	[blindId: string]: BoundingBoxSettings;
}

export interface RootMeshesByBlindType {
	outdoor: AbstractMesh;
	interior: AbstractMesh;
}

export interface GizmoMinMaxMeshScaling {
	min: { x: number; y: number };
	max: { x: number; y: number };
}

export interface ZoomGizmoSettings {
	fingers: number;
	pickOnMesh: boolean;
}

export interface QuizCloseButton {
	status: boolean;
	buttonType?: string;
}

export interface ShutterValues {
	id: string;
	value: number;
}

interface TaskbarButton {
	type: string;
	text: string;
}

export interface TaskbarItem {
	title: string;
	id: string;
	active: boolean;
	status: null | boolean;
	button: TaskbarButton;
}

export type BlindScreenShots = {
	name: string;
	image: string;
};

export interface ScreenshotObserve {
	img: string;
	blindId?: number;
}

export interface ScreenShotsObjects {
	sceneScreenShot: string;
	blindScreenShots?: BlindScreenShots;
}

export interface ImageVisualisationSettings {
	height: number;
	width: number;
	type: string | null | undefined;
	index: number | null | undefined;
	image: string;
	shutterValues?: { [blindId: string]: number };
	settings?: BoundingBoxesSettings;
}

export interface SVGData {
	name: string;
	file: SafeResourceUrl;
}

export interface BackgroundConfig {
	src: string;
	night_src: string;
	width: number;
	height: number;
	type: string;
	index: number;
	theme: string;
	image_orientation: string;
	id: string;
	image?: string | ArrayBuffer;
	base64: string | ArrayBuffer;
	blob: File | Blob;
	shutterValues?: { [blindId: string]: number };
	settings?: BoundingBoxesSettings;
	sampleProjectBlinds?: { [blindId: string]: number };
	cropped_src: string;
	cropper_settings: CropperSettings;
}

export interface ImageCategory {
	preview: string[];
	landscape: string[];
	portrait: string[];
	origin: string[];
}

export interface ProjectSamplesImages {
	outdoor: {
		day: ImageCategory;
		night: ImageCategory;
	};
	interior: {
		day: ImageCategory;
	};
}

export interface LoadedImageData {
	src: string;
	width: number;
	height: number;
}

export interface ImageSize {
	width: number;
	height: number;
}

export interface CroppedBackgroundConfig {
	cropped_src: string;
	width: number;
	height: number;
}

export interface IVSceneSettings {
	sample: BackgroundConfig;
	upload: BackgroundConfig;
	iv_last_type: string;
	device: string;
	openings: Opening[]; // TEMP // Remove when backend updated to handle saving opening data properly
	detectionResult: StoredDetectionResults;
}

export interface IBlindSelect {
	id: number | string;
	type?: string;
	status?: string;
}

export interface Country {
	code: string;
	name: string;
	data_store: string;
	link: string;
}

export interface FarSettings {
	country: string;
	country_code: string;
	country_latitude: number;
	country_longitude: number;
	description_for_postcode: string;
	description_for_retailer_name: string;
	filters_are_enabled: boolean;
	max_non_premium_distance: number;
	max_premium_distance: number;
	non_premium_max_additional_search_quantity: number;
	non_premium_number: number;
	number_of_retailers_can_be_selected: number;
	premium_display: boolean;
	premium_number: number;
	show_premium_retailers_only: boolean;
	standart_non_premium_distance: number;
	subtitle: string;
	title: string;
	duplicate_premium_retailers: null | string;
}

export enum CropperAction {
	FLIP = 'FLIP',
	ROTATE = 'ROTATE',
	ZOOM_IN = 'ZOOM_IN',
	ZOOM_OUT = 'ZOOM_OUT',
	RESET = 'RESET',
	DELETE = 'DELETE',
}

export interface CropperSettings {
	cropper_data: Cropper.Data;
	cropper_box_data: Cropper.CropBoxData;
	cropper_canvas_data: Cropper.CanvasData;
	cropper_container_data: Cropper.ContainerData;
}

export interface Rating {
	id: string;
	score: number;
}

export interface FeedbackData {
	session_key: string;
	rating: number;
	feedback?: string;
}

export interface PostDataPayload {
	file_type?: string;
	file_key?: string;
	task_uuid?: string;
	type?: string;
	additional_parameter?: {
		is_indoor?: number;
	};
	sha256?: string;
}

export interface DetectionApiResponse<T = unknown> {
	status: 0 | 1;
	msg: string;
	data: T | null;
	token: string;
}

export interface PresignedUrlResponse {
	key: string;
	url: string;
	file_already_uploaded: boolean;
}

export interface StoredDetectionResults {
	results: DetectionResult;
	timeout?: boolean;
	imageSize?: { width: number; height: number };
}

export interface Point {
	x: number;
	y: number;
}

export interface Opening {
	id: number;
	coordinates: Point[];
	isSelected: boolean;
	isFitted: boolean;
	blindId: number | null;
	displayNumber: number | null;
	name: string | null;
	blindType: string | null;
}

export interface ModalBlindData {
	blindId: number;
	displayNumber: number;
	selected: boolean;
	type: string;
	name: string;
	openingId?: number;
}

export type DetectionPoint = { x: number; y: number }; // Not sure if this is the correct way or place to put these.
export type DetectionResult = Array<Array<DetectionPoint>> | null;

export interface ActionData {
	session_key: string;
	action_type: string;
	element: string;
	page_url: string;
	device_info: string;
	created_at: Date;
}

export interface NodeRef {
	group: SVGGElement;
	hit: SVGCircleElement;
	dot: SVGCircleElement;
}

export interface BlindDisplayStatus {
	show: boolean;
	previouslyAssigned: boolean;
}

export interface PhotoForm {
	photo: FormControl<string>;
	photo_base64: FormControl<string | ArrayBuffer | null>;
	photo_src: FormControl<string | null>;
	photo_blob: FormControl<Blob | null>;
	cropped_src: FormControl<string | null>;
	night_src: FormControl<string | null>;
}
